import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Button, Tabs, Tab, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import omsapi from "../../services/omsapi";

library.add(fas);

const defaultStatusMap = {
  1: "Pending",
  2: "Confirmed",
  3: "Admin Cancel",
  4: "Packed",
  5: "Shipped",
  6: "Delivered",
  7: "Customer Cancel",
  8: "Customer Refund",
  9: "Deleted",
  10: "Refund Approved",
  11: "Feedback",
};

const defaultDeliveryType = {
  1: "Standard Delivery",
  2: "Express Delivery",
  3: "Take Away",
};

const toneClass = (code) => {
  switch (code) {
    case 1:
      return "badge text-bg-warning";
    case 2:
      return "badge text-bg-primary";
    case 3:
      return "badge text-bg-danger";
    case 4:
      return "badge text-bg-info";
    case 5:
      return "badge text-bg-secondary";
    case 6:
      return "badge text-bg-success";
    case 7:
      return "badge text-bg-danger";
    case 8:
      return "badge text-bg-warning";
    case 9:
      return "badge text-bg-dark";
    case 10:
      return "badge text-bg-success";
    case 11:
      return "badge text-bg-secondary";
    default:
      return "badge text-bg-light";
  }
};

const fmt = (iso) => {
  if (!iso) return "-";
  const dt = moment(iso);
  if (!dt.isValid()) return "-";
  return dt.utcOffset("+05:30").format("YYYY/MM/DD hh:mm:ss A");
};

const toTitleCase = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return text
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

const toText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
};

const uniqBy = (list, keyBuilder) => {
  const seen = new Set();
  const output = [];
  for (const item of list) {
    const key = keyBuilder(item);
    if (!key) {
      output.push(item);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    output.push(item);
  }
  return output;
};

const findValue = (obj, keys = []) => {
  if (!obj || typeof obj !== "object") return undefined;
  const entries = Object.entries(obj);
  for (const key of keys) {
    const matched = entries.find(([entryKey]) => entryKey.toLowerCase() === key.toLowerCase());
    if (matched && matched[1] !== undefined && matched[1] !== null) {
      return matched[1];
    }
  }
  return undefined;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseJsonSafely = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw || (!raw.startsWith("{") && !raw.startsWith("["))) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const extractName = (source) => {
  if (!source) return "";
  if (typeof source === "string") return toText(source);
  if (typeof source === "object") {
    return toText(
      findValue(source, ["name", "fullName", "employeeName", "userName", "operatorName", "label"])
    );
  }
  return "";
};

const formatHistoryNotes = (orderData) => {
  const parsed = parseJsonSafely(orderData);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
    return [toText(orderData) || "-"];
  }

  const workflow = toText(findValue(parsed, ["workflow", "flow"]));
  const action = toText(findValue(parsed, ["action", "event", "type"]));
  const message = toText(findValue(parsed, ["message", "note", "remarks", "comment"]));
  const actionToken = action.toUpperCase().replace(/\s+/g, "_");
  const assignedName = extractName(
    findValue(parsed, [
      "assignedEmployeeName",
      "assignedEmployee",
      "assignee",
      "assignedTo",
      "employee",
      "operator",
      "user",
    ])
  );

  const lines = [];
  if (workflow) lines.push(`Workflow: ${toTitleCase(workflow)}`);
  if (action) lines.push(`Action: ${toTitleCase(action)}`);
  if (actionToken === "ASSIGNED" && assignedName) {
    lines.push(`Assigned Employee: ${assignedName}`);
  }
  if (message) lines.push(`Message: ${message}`);

  return lines.length ? lines : ["Details updated."];
};

const statusLabel = (label) => {
  const text = toText(label);
  return text.startsWith("#") ? text : toTitleCase(text);
};

const getCompletedLabelByMode = (modeKey) => {
  const normalizedMode = toText(modeKey).toLowerCase();
  if (normalizedMode === "picking") return "Picked";
  if (normalizedMode === "packing") return "Packed";
  return "Completed";
};

const mapOperationStatusCode = (statusCode, modeKey) => {
  switch (statusCode) {
    case 1:
      return { key: "pending", label: "Pending", bg: "secondary" };
    case 2:
      return { key: "inprogress", label: "In Progress", bg: "primary" };
    case 3:
      return { key: "completed", label: getCompletedLabelByMode(modeKey), bg: "success" };
    case 4:
      return { key: "paused", label: "Paused", bg: "warning", text: "dark" };
    case 5:
      return { key: "cancelled", label: "Cancelled", bg: "danger" };
    default:
      return null;
  }
};

const getOperationStatusMeta = (rawStatus, modeKey) => {
  const numericStatus = toNumber(rawStatus);
  if (Number.isInteger(numericStatus)) {
    const mappedByCode = mapOperationStatusCode(numericStatus, modeKey);
    if (mappedByCode) return mappedByCode;
  }

  const normalizedText = toText(rawStatus);
  if (!normalizedText || normalizedText === "-") return { key: "unknown", label: "-", bg: "secondary" };

  const token = normalizedText.toUpperCase().replace(/[\s-]+/g, "_");
  if (token.includes("PENDING")) return mapOperationStatusCode(1, modeKey);
  if (token.includes("IN_PROGRESS") || token.includes("ACTIVE") || token.includes("STARTED")) {
    return mapOperationStatusCode(2, modeKey);
  }
  if (token.includes("PICKED") || token.includes("PACKED") || token.includes("COMPLETED") || token.includes("DONE")) {
    return mapOperationStatusCode(3, modeKey);
  }
  if (token.includes("PAUSED") || token.includes("HOLD")) return mapOperationStatusCode(4, modeKey);
  if (token.includes("CANCEL")) return mapOperationStatusCode(5, modeKey);

  return { key: "unknown", label: statusLabel(normalizedText), bg: "secondary" };
};

const getItemStatusMeta = (rawStatus, modeKey) => getOperationStatusMeta(rawStatus, modeKey);

const mapScanResultCode = (resultCode) => {
  switch (resultCode) {
    case 1:
      return { key: "success", label: "Success", bg: "success" };
    case 2:
      return { key: "wrong", label: "Wrong Item", bg: "danger" };
    case 3:
      return { key: "notfound", label: "Not Found", bg: "warning", text: "dark" };
    case 4:
      return { key: "extra", label: "Extra Scan", bg: "dark" };
    default:
      return null;
  }
};

const getScanResultMeta = (scan) => {
  const rawResult = findValue(scan, ["scanResult", "scan_result", "result", "status"]);
  const numericResult = toNumber(rawResult);
  if (Number.isInteger(numericResult)) {
    const mappedByCode = mapScanResultCode(numericResult);
    if (mappedByCode) return mappedByCode;
  }

  const resultToken = toText(rawResult).toUpperCase();
  const messageToken = toText(findValue(scan, ["message", "remarks", "note"])).toUpperCase();
  const combined = `${resultToken} ${messageToken}`;

  if (combined.includes("SUCCESS") || combined.includes("MATCH")) return mapScanResultCode(1);
  if (combined.includes("WRONG") || combined.includes("MISMATCH") || combined.includes("INVALID")) {
    return mapScanResultCode(2);
  }
  if (combined.includes("NOT_FOUND") || combined.includes("NOT FOUND") || combined.includes("MISSING")) {
    return mapScanResultCode(3);
  }
  if (combined.includes("EXTRA") || combined.includes("OVER")) return mapScanResultCode(4);

  return { key: "unknown", label: statusLabel(toText(rawResult) || "Unknown"), bg: "secondary" };
};

const normalizeToken = (value) => toText(value).toUpperCase().replace(/[\s-]+/g, "_");

const getSessionId = (session) => {
  const sessionId = findValue(session, ["sessionId", "session_id", "id"]);
  if (sessionId === undefined || sessionId === null || sessionId === "") return null;
  return String(sessionId);
};

const getSessionStatusRaw = (session) => findValue(session, ["sessionStatus", "session_status", "status"]);

const getSessionTimeValue = (session) => {
  const dtRaw = findValue(session, [
    "completedAt",
    "completedTime",
    "endedAt",
    "endTime",
    "updatedAt",
    "updatedTime",
    "createdAt",
    "startedAt",
    "startTime",
  ]);
  const dt = moment(dtRaw);
  return dt.isValid() ? dt.valueOf() : 0;
};

const getItemSessionId = (item) => {
  const direct = findValue(item, ["sessionId", "session_id", "sessionLogId", "session_log_id"]);
  if (direct !== undefined && direct !== null && direct !== "") return String(direct);
  const nestedSession = findValue(item, ["session"]);
  if (nestedSession && typeof nestedSession === "object") return getSessionId(nestedSession);
  return null;
};

const getIdentityTokens = (obj, keys) =>
  keys
    .map((key) => toText(findValue(obj, [key])))
    .map((value) => value.toLowerCase())
    .filter(Boolean);

const isAddItemAction = (changeLog) => {
  const action = normalizeToken(findValue(changeLog, ["actionType", "action_type", "action"]));
  return action === "ADD_ITEM";
};

const doesChangeLogMatchItem = (changeLog, item) => {
  const itemTokens = new Set([
    ...getIdentityTokens(item, [
      "id",
      "itemId",
      "productItemId",
      "productItemCode",
      "item_ref_code",
      "orderLineId",
      "order_line_id",
    ]),
    ...getIdentityTokens(item, ["productName", "orderProductName", "product", "name"]),
  ]);

  const changeTokens = [
    ...getIdentityTokens(changeLog, [
      "id",
      "itemId",
      "productItemId",
      "productItemCode",
      "item_ref_code",
      "orderLineId",
      "order_line_id",
    ]),
    ...getIdentityTokens(changeLog, ["productName", "orderProductName", "product", "name"]),
  ];

  for (const token of changeTokens) {
    if (itemTokens.has(token)) return true;
  }
  return false;
};

const getChangeActionMeta = (actionRaw) => {
  const action = normalizeToken(actionRaw);
  if (action === "ADD_ITEM") return { label: "Added Item", bg: "success" };
  if (action === "REMOVE") return { label: "Removed Item", bg: "danger" };
  if (action === "REMOVE_ITEM") return { label: "Removed Item", bg: "danger" };
  if (action === "QTY_UPDATE") return { label: "Qty Updated", bg: "primary" };
  if (action === "QTY_CHANGE") return { label: "Qty Changed", bg: "primary" };
  return { label: statusLabel(toText(actionRaw) || "-"), bg: "secondary" };
};

const normalizeOperation = (operation, modeKey) => {
  if (!operation) {
    return {
      sessions: [],
      items: [],
      scans: [],
      itemChangeLogs: [],
      summary: {
        status: "-",
        statusLabel: "",
        operator: "-",
        started: "-",
        completed: "-",
        totalScans: 0,
        success: 0,
        wrong: 0,
        extra: 0,
        notFound: 0,
      },
      hasData: false,
    };
  }

  const sessionsSource = toArray(
    findValue(operation, ["sessions", "sessionList", "sessionLogs", "timeline", "activity"])
  );
  const highlightedSessions = toArray(
    findValue(operation, ["currentSession", "latestSession", "activeSession"])
  );
  const sessions = uniqBy([...highlightedSessions, ...sessionsSource], (session) => {
    const sessionId = getSessionId(session);
    return sessionId ? `session-${sessionId}` : null;
  });

  const sortedSessions = [...sessions].sort((a, b) => getSessionTimeValue(b) - getSessionTimeValue(a));
  const selectedCompletedSession = sortedSessions.find(
    (session) => getOperationStatusMeta(getSessionStatusRaw(session), modeKey).key === "completed"
  );
  const selectedActiveSession = sortedSessions.find((session) =>
    ["inprogress", "paused"].includes(getOperationStatusMeta(getSessionStatusRaw(session), modeKey).key)
  );
  const selectedSession = selectedCompletedSession || selectedActiveSession || sortedSessions[0] || null;
  const selectedSessionId = getSessionId(selectedSession);

  const rootItems = toArray(
    findValue(operation, ["itemProgress", "itemLogs", "items", "products", "productProgress"])
  );
  const selectedSessionItems = selectedSession
    ? toArray(findValue(selectedSession, ["itemProgress", "itemLogs", "items", "products", "productProgress"]))
    : [];
  let items = rootItems.length
    ? rootItems
    : sessions.flatMap((session) =>
        toArray(findValue(session, ["itemProgress", "itemLogs", "items", "products", "productProgress"]))
      );
  if (selectedSessionItems.length) {
    items = [...selectedSessionItems, ...items];
  }
  items = uniqBy(items, (item) => {
    const itemId = findValue(item, ["id", "itemId"]);
    if (itemId !== undefined && itemId !== null && itemId !== "") return `item-${String(itemId)}`;

    const orderLineId = findValue(item, ["orderLineId", "order_line_id"]);
    const productItemId = findValue(item, ["productItemId", "product_item_id"]);
    const sessionId = findValue(item, ["sessionId", "session_id"]);
    if (
      orderLineId !== undefined &&
      orderLineId !== null &&
      orderLineId !== "" &&
      productItemId !== undefined &&
      productItemId !== null &&
      productItemId !== "" &&
      sessionId !== undefined &&
      sessionId !== null &&
      sessionId !== ""
    ) {
      return `composite-${String(orderLineId)}-${String(productItemId)}-${String(sessionId)}`;
    }

    return null;
  });
  if (selectedSessionId) {
    const selectedItems = items.filter((item) => getItemSessionId(item) === selectedSessionId);
    if (selectedItems.length) items = selectedItems;
  }

  let scans = toArray(findValue(operation, ["scanLogs", "scanLog", "scans", "scanHistory"]));
  if (!scans.length) {
    scans = sessions.flatMap((session) =>
      toArray(findValue(session, ["scanLogs", "scanLog", "scans", "scanHistory"]))
    );
  }

  let itemChangeLogs = toArray(findValue(operation, ["itemChangeLogs", "itemChanges", "changeLogs"]));
  if (!itemChangeLogs.length) {
    itemChangeLogs = sessions.flatMap((session) =>
      toArray(findValue(session, ["itemChangeLogs", "itemChanges", "changeLogs"]))
    );
  }
  itemChangeLogs = uniqBy(itemChangeLogs, (changeLog) => {
    const changeId = findValue(changeLog, ["changeId", "id"]);
    if (changeId !== undefined && changeId !== null && changeId !== "") return `change-${String(changeId)}`;
    const action = toText(findValue(changeLog, ["actionType", "action_type", "action"]));
    const dt = toText(findValue(changeLog, ["time", "changedAt", "createdAt", "updatedAt"]));
    const product = toText(findValue(changeLog, ["productName", "orderProductName", "product", "name"]));
    return action || dt || product ? `change-${action}-${dt}-${product}` : null;
  });
  if (selectedSessionId) {
    const scopedChangeLogs = itemChangeLogs.filter((changeLog) => {
      const changeSessionId = findValue(changeLog, ["sessionId", "session_id", "sessionLogId", "session_log_id"]);
      if (changeSessionId === undefined || changeSessionId === null || changeSessionId === "") return false;
      return String(changeSessionId) === selectedSessionId;
    });
    if (scopedChangeLogs.length) itemChangeLogs = scopedChangeLogs;
  }

  items = items.map((item) => {
    const isAddedByPicker = Boolean(findValue(item, ["isAddedByPicker", "is_added_by_picker"]));
    const matchedAddChange = itemChangeLogs.find(
      (changeLog) => isAddItemAction(changeLog) && doesChangeLogMatchItem(changeLog, item)
    );
    const isPickerAdded = isAddedByPicker || Boolean(matchedAddChange);
    const reasonFromChange = toText(findValue(matchedAddChange, ["reason", "remarks", "note", "message"])) || "-";

    return {
      ...item,
      __isPickerAdded: isPickerAdded,
      __addedReason: isPickerAdded ? reasonFromChange : "",
    };
  });

  const summarySource = findValue(operation, ["summary", "stats", "totals"]) || selectedSession || operation;

  const scanMeta = scans.map((scan) => getScanResultMeta(scan));
  const successFromScans = scanMeta.filter((meta) => meta.key === "success").length;
  const wrongFromScans = scanMeta.filter((meta) => meta.key === "wrong").length;
  const extraFromScans = scanMeta.filter((meta) => meta.key === "extra").length;
  const notFoundFromScans = scanMeta.filter((meta) => meta.key === "notfound").length;

  const summary = {
    status:
      findValue(summarySource, ["sessionStatus", "session_status", "status"]) ??
      findValue(selectedSession, ["sessionStatus", "session_status", "status"]) ??
      "-",
    statusLabel:
      toText(
        findValue(summarySource, ["statusLabel", "status_label", "sessionStatusLabel", "session_status_label"])
      ) ||
      toText(
        findValue(selectedSession, ["statusLabel", "status_label", "sessionStatusLabel", "session_status_label"])
      ) ||
      "",
    operator:
      extractName(
        findValue(summarySource, ["operator", "operatorName", "employee", "employeeName", "user", "assignedTo"])
      ) || "-",
    started:
      findValue(summarySource, ["startedAt", "startTime", "startedTime", "createdAt", "sessionStart"]) || null,
    completed:
      findValue(summarySource, ["completedAt", "completedTime", "endedAt", "endTime", "sessionEnd"]) || null,
    totalScans:
      toNumber(findValue(summarySource, ["totalScans", "scanCount", "totalScanCount"])) ?? scans.length ?? 0,
    success: toNumber(findValue(summarySource, ["successCount", "success", "scansSuccess"])) ?? successFromScans,
    wrong: toNumber(findValue(summarySource, ["wrongCount", "wrong", "wrongItemCount"])) ?? wrongFromScans,
    extra: toNumber(findValue(summarySource, ["extraCount", "extra", "extraScanCount"])) ?? extraFromScans,
    notFound:
      toNumber(findValue(summarySource, ["notFoundCount", "notFound", "missingCount"])) ?? notFoundFromScans,
  };

  return {
    sessions,
    items,
    scans,
    itemChangeLogs,
    summary,
    hasData: Boolean(sessions.length || items.length || scans.length || itemChangeLogs.length),
  };
};

const OrderHistoryPage = () => {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [branch, setBranch] = useState(null);
  const [history, setHistory] = useState([]);
  const [operations, setOperations] = useState({ picking: null, packing: null });
  const [statusMap, setStatusMap] = useState(defaultStatusMap);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const json = await omsapi.getOrderHistory(orderId);
        if (json?.status !== "SUCCESS") throw new Error("Failed to load");

        const payload = json.data?.data || {};
        setOrder(payload.order || null);
        setBranch(payload.branchDet || null);
        setHistory(payload.history || []);
        setOperations(payload.operations || { picking: null, packing: null });

        const map = json.OrderStatusData || {};
        const numMap = Object.fromEntries(Object.entries(map).map(([key, value]) => [Number(key), value]));
        setStatusMap({ ...defaultStatusMap, ...numMap });
      } catch (e) {
        setError(e?.message || "Error loading history");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [orderId]);

  const sortedHistory = useMemo(
    () => [...history].sort((a, b) => new Date(b.actionDate) - new Date(a.actionDate)),
    [history]
  );

  const pickingData = useMemo(() => normalizeOperation(operations?.picking, "picking"), [operations]);
  const packingData = useMemo(() => normalizeOperation(operations?.packing, "packing"), [operations]);

  const renderOperationTab = (operationData, modeLabel, emptyMessage) => {
    if (!operationData.hasData) {
      return <div className="text-muted py-4">{emptyMessage}</div>;
    }

    const modeKey = toText(modeLabel).toLowerCase();
    const explicitSummaryLabel = toText(operationData.summary.statusLabel);
    const hasExplicitSummaryLabel = Boolean(explicitSummaryLabel && !/^\d+$/.test(explicitSummaryLabel));
    const summaryStatusMeta = hasExplicitSummaryLabel
      ? { ...getOperationStatusMeta(operationData.summary.status, modeKey), label: statusLabel(explicitSummaryLabel) }
      : getOperationStatusMeta(operationData.summary.status, modeKey);

    return (
      <div className="d-flex flex-column gap-3 py-3">
        <Row className="g-3">
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Session Status</div>
                <Badge bg={summaryStatusMeta.bg} text={summaryStatusMeta.text}>
                  {summaryStatusMeta.label}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Operator Name</div>
                <div>{operationData.summary.operator || "-"}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Started Time</div>
                <div>{fmt(operationData.summary.started)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Completed Time</div>
                <div>{fmt(operationData.summary.completed)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Total Scans</div>
                <div className="fw-semibold">{operationData.summary.totalScans ?? 0}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Success / Wrong</div>
                <div>
                  <Badge className="bg-success me-1">{operationData.summary.success ?? 0}</Badge>
                  <Badge className="bg-danger">{operationData.summary.wrong ?? 0}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} md={4} lg={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Extra / Not Found</div>
                <div>
                  <Badge className="bg-secondary me-1">{operationData.summary.extra ?? 0}</Badge>
                  <Badge className="bg-warning text-dark">{operationData.summary.notFound ?? 0}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white fw-semibold">Sessions</Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Session ID</th>
                    <th>Status</th>
                    <th>Operator</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Last Action</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {operationData.sessions.length ? (
                    operationData.sessions.map((session, index) => {
                      const rawSessionStatus = findValue(session, ["sessionStatus", "session_status", "status"]);
                      const sessionStatusMeta = getOperationStatusMeta(rawSessionStatus, modeKey);
                      const operatorName =
                        extractName(
                          findValue(session, [
                            "operator",
                            "operatorName",
                            "employee",
                            "employeeName",
                            "user",
                            "assignedTo",
                          ])
                        ) || "-";
                      const notesValue = findValue(session, ["notes", "message", "remarks", "comment"]);

                      return (
                        <tr key={findValue(session, ["sessionId", "id", "sessionCode"]) || `session-${index}`}>
                          <td>{toText(findValue(session, ["sessionId", "id", "sessionCode"])) || "-"}</td>
                          <td>
                            <Badge bg={sessionStatusMeta.bg} text={sessionStatusMeta.text}>
                              {sessionStatusMeta.label}
                            </Badge>
                          </td>
                          <td>{operatorName}</td>
                          <td>{fmt(findValue(session, ["startedAt", "startTime", "startedTime", "createdAt"]))}</td>
                          <td>{fmt(findValue(session, ["completedAt", "completedTime", "endedAt", "endTime"]))}</td>
                          <td>{toText(findValue(session, ["lastAction", "action", "recentAction"])) || "-"}</td>
                          <td>{toText(notesValue) || "-"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-muted text-center py-3">
                        No sessions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white fw-semibold">Item Progress</Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Item ID</th>
                    <th>Expected Qty</th>
                    <th>Picked/Packed Qty</th>
                    <th>Status</th>
                    <th>Updated Time</th>
                  </tr>
                </thead>
                <tbody>
                  {operationData.items.length ? (
                    operationData.items.map((item, index) => {
                      const itemStatusMeta = getItemStatusMeta(
                        findValue(item, ["itemStatus", "item_status", "status"]),
                        modeKey
                      );
                      return (
                        <tr
                          key={findValue(item, ["itemId", "id", "productId", "sku"]) || `item-${index}`}
                          className={item.__isPickerAdded ? "table-warning" : ""}
                        >
                          <td>
                            {toText(
                              findValue(item, ["productName", "orderProductName", "product", "name"])
                            ) || "-"}
                            {item.__isPickerAdded && (
                              <div className="mt-1">
                                <Badge bg="warning" text="dark" className="me-2">
                                  Added by Picker
                                </Badge>
                                <div className="small text-muted mt-1">Reason: {item.__addedReason}</div>
                              </div>
                            )}
                          </td>
                          <td>
                            {toText(
                              findValue(item, ["itemId", "productItemCode", "productItemId", "item_ref_code"])
                            ) || "-"}
                          </td>
                          <td>{toText(findValue(item, ["expectedQty", "expected_qty"])) || "0"}</td>
                          <td>
                            {toText(
                              findValue(item, ["pickedQty", "picked_qty", "packedQty", "packed_qty"])
                            ) || "0"}
                          </td>
                          <td>
                            <Badge bg={itemStatusMeta.bg} text={itemStatusMeta.text}>
                              {itemStatusMeta.label}
                            </Badge>
                          </td>
                          <td>{fmt(findValue(item, ["updatedAt", "updated_at"]))}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-muted text-center py-3">
                        No {modeLabel.toLowerCase()} item progress found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white fw-semibold">Item Changes</Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Action</th>
                    <th>Product</th>
                    <th>Old Qty</th>
                    <th>New Qty</th>
                    <th>Reason</th>
                    <th>Changed By</th>
                  </tr>
                </thead>
                <tbody>
                  {operationData.itemChangeLogs.length ? (
                    operationData.itemChangeLogs.map((changeLog, index) => {
                      const actionRaw = findValue(changeLog, ["actionType", "action_type", "action"]);
                      const actionMeta = getChangeActionMeta(actionRaw);
                      return (
                        <tr key={findValue(changeLog, ["changeId", "id"]) || `change-${index}`}>
                          <td>
                            {fmt(
                              findValue(changeLog, [
                                "time",
                                "changedAt",
                                "createdAt",
                                "updatedAt",
                                "actionTime",
                                "updated_at",
                              ])
                            )}
                          </td>
                          <td>
                            <Badge bg={actionMeta.bg}>{actionMeta.label}</Badge>
                          </td>
                          <td>
                            {toText(
                              findValue(changeLog, ["productName", "orderProductName", "product", "name"])
                            ) || "-"}
                          </td>
                          <td>{toText(findValue(changeLog, ["oldQty", "old_qty", "previousQty", "prevQty"])) || "-"}</td>
                          <td>{toText(findValue(changeLog, ["newQty", "new_qty", "qty", "currentQty"])) || "-"}</td>
                          <td>{toText(findValue(changeLog, ["reason", "remarks", "note", "message"])) || "-"}</td>
                          <td>
                            {extractName(
                              findValue(changeLog, ["changedBy", "updatedBy", "user", "operator", "employee"])
                            ) || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-muted text-center py-3">
                        No item changes found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white fw-semibold">Scan Log</Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>EAN</th>
                    <th>Product</th>
                    <th>Result</th>
                    <th>Message</th>
                    <th>Scanned By</th>
                  </tr>
                </thead>
                <tbody>
                  {operationData.scans.length ? (
                    operationData.scans.map((scan, index) => {
                      const scanResultMeta = getScanResultMeta(scan);
                      return (
                        <tr key={findValue(scan, ["scanId", "id", "ean"]) || `scan-${index}`}>
                          <td>{fmt(findValue(scan, ["time", "scanTime", "createdAt", "updatedAt"]))}</td>
                          <td>{toText(findValue(scan, ["ean", "barcode", "code"])) || "-"}</td>
                          <td>{toText(findValue(scan, ["product", "productName", "name"])) || "-"}</td>
                          <td>
                            <Badge bg={scanResultMeta.bg} text={scanResultMeta.text}>
                              {scanResultMeta.label}
                            </Badge>
                          </td>
                          <td>{toText(findValue(scan, ["message", "remarks", "note"])) || "-"}</td>
                          <td>
                            {extractName(findValue(scan, ["scannedBy", "operator", "employee", "user"])) || "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-muted text-center py-3">
                        No {modeLabel.toLowerCase()} scan logs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <Container className="py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="m-0">
          <FontAwesomeIcon icon="fa-solid fa-clock-rotate-left" className="me-2" />
          Order History
        </h4>
        <div className="d-flex gap-2">
          <Button as={Link} to="/admin/orders/all" variant="outline-secondary" size="sm">
            <FontAwesomeIcon icon="fa-solid fa-arrow-left" className="me-2" />
            Back to Orders
          </Button>
        </div>
      </div>

      {loading && <div className="text-muted">Loading…</div>}
      {error && <div className="text-danger">Error: {error}</div>}

      {!loading && !error && order && (
        <>
          <Card className="mb-3 shadow-sm border-0">
            <Card.Body>
              <Row className="gy-2">
                <Col md={3}>
                  <strong>Order ID</strong>
                  <div>#{order.id}</div>
                </Col>
                <Col md={3}>
                  <strong>Show ID</strong>
                  <div>{order.showId}</div>
                </Col>
                <Col md={3}>
                  <strong>Status</strong>
                  <div>
                    <span className={toneClass(order.status)}>
                      {statusLabel(statusMap[order.status] || order.status)}
                    </span>
                  </div>
                </Col>
                <Col md={3}>
                  <strong>Branch</strong>
                  <div>{branch?.branchName ?? "-"}</div>
                </Col>

                <Col md={3}>
                  <strong>Delivery Type</strong>
                  <div>{defaultDeliveryType[order.deliveryType] ?? "-"}</div>
                </Col>
                <Col md={3}>
                  <strong>Delivery Window</strong>
                  <div>
                    {order.deliveryDate
                      ? `${order.deliveryDate} ${order.deliveryFromTime} - ${order.deliveryToTime}`
                      : "-"}
                  </div>
                </Col>
                <Col md={3}>
                  <strong>Total</strong>
                  <div>{order.totalAmount?.toFixed?.(2) ?? order.totalAmount ?? "-"}</div>
                </Col>

                <Col md={3}>
                  <strong>Posted</strong>
                  <div>{fmt(order.postedDate)}</div>
                </Col>
                <Col md={3}>
                  <strong>Confirmed</strong>
                  <div>{fmt(order.confirmedDate)}</div>
                </Col>
                <Col md={3}>
                  <strong>Cancelled</strong>
                  <div>{fmt(order.cancelledDate)}</div>
                </Col>
                <Col md={3}>
                  <strong>Delivered</strong>
                  <div>{fmt(order.deliveredDate)}</div>
                </Col>

                <Col md={12}>
                  <strong>Order Reason</strong>
                  <div>{order.orderReason || "-"}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3 shadow-sm border-0">
            <Card.Header className="bg-white d-flex align-items-center gap-2">
              <FontAwesomeIcon icon="fa-solid fa-list" />
              <span>Status Trail</span>
            </Card.Header>
            <Card.Body className="p-0">
              {sortedHistory.length === 0 ? (
                <div className="p-3 text-muted">No history found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "28%" }}>When (IST)</th>
                        <th style={{ width: "22%" }}>Status</th>
                        <th style={{ width: "15%" }}>By (User)</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td>{fmt(entry.actionDate)}</td>
                          <td>
                            <span className={toneClass(entry.orderStatus)}>
                              {statusLabel(statusMap[entry.orderStatus] || `#${entry.orderStatus}`)}
                            </span>
                          </td>
                          <td>{entry.userName ?? "-"}</td>
                          <td>
                            <div className="d-flex flex-column">
                              {formatHistoryNotes(entry.orderData).map((line, lineIndex) => (
                                <span
                                  key={`${entry.id}-line-${lineIndex}`}
                                  className={lineIndex > 0 ? "small text-muted" : ""}
                                >
                                  {line}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white d-flex align-items-center gap-2">
              <FontAwesomeIcon icon="fa-solid fa-warehouse" />
              <span>Operations Timeline</span>
            </Card.Header>
            <Card.Body>
              <Tabs defaultActiveKey="picking" id="operations-timeline-tabs" className="mb-3">
                <Tab
                  eventKey="picking"
                  title={
                    <span>
                      <FontAwesomeIcon icon="fa-solid fa-hand" className="me-2" />
                      Picking
                    </span>
                  }
                >
                  {renderOperationTab(pickingData, "Picking", "No picking activity found")}
                </Tab>
                <Tab
                  eventKey="packing"
                  title={
                    <span>
                      <FontAwesomeIcon icon="fa-solid fa-box-open" className="me-2" />
                      Packing
                    </span>
                  }
                >
                  {renderOperationTab(packingData, "Packing", "No packing activity found")}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default OrderHistoryPage;
